import React, { useState, useEffect } from 'react';
import { fetchInfluencers, getCampaigns, getCampaign, createCampaign, sendCampaign, getUser } from '../api';
import { PLATFORMS, TIERS, CHORD, TIER_COLORS, fmtK } from '../constants';

const NICHES = ['Mom','Fashion','Tech','Fitness','Food','Travel','Lifestyle','Beauty','Gaming','Finance','Education','Entertainment','Healthcare','Other'];

const DEFAULT_SUBJECT = 'Exciting Brand Collaboration — Chord Agency';
const DEFAULT_BODY = `Hi {{name}},

Hope you're doing well!

We have an exciting brand collaboration opportunity that we think would be a great fit for you.

We're working with [Brand Name] — [one line about the brand] — and we'd love to have you on board for this campaign.

Here's what the collaboration would look like:
• Platform: [Instagram / YouTube / etc.]
• Deliverables: [Reels / Stories / Posts]
• Timeline: [Campaign dates]
• Compensation: [Rate / Barter / etc.]

If this sounds interesting, please reply to this email and we'll share the full brief.

Looking forward to working with you!

Warm regards,
The Chord Agency Team`;

function StatBox({ label, value, color, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: `${color}15`, border: `0.5px solid ${color}40`,
      borderRadius: 10, padding: '14px 20px', textAlign: 'center',
      cursor: onClick ? 'pointer' : 'default', flex: 1, minWidth: 100,
      transition: 'all 0.15s',
    }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.background = `${color}25`; }}
    onMouseLeave={e => { if (onClick) e.currentTarget.style.background = `${color}15`; }}>
      <div style={{ fontSize: 26, fontWeight: 700, color }}>{value ?? 0}</div>
      <div style={{ fontSize: 11, color: CHORD.textMuted, textTransform: 'uppercase', letterSpacing: '.7px', marginTop: 2 }}>{label}</div>
      {onClick && <div style={{ fontSize: 10, color: `${color}80`, marginTop: 3 }}>click for details</div>}
    </div>
  );
}

function DetailModal({ campaign, onClose }) {
  const [detail, setDetail]   = useState(null);
  const [filter, setFilter]   = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCampaign(campaign.id).then(d => { setDetail(d); setLoading(false); }).catch(() => setLoading(false));
  }, [campaign.id]);

  const recipients = detail?.recipients || [];
  const filtered   = filter === 'all' ? recipients : 
    filter === 'delivered' ? recipients.filter(r => r.delivered_at) :
    filter === 'opened'    ? recipients.filter(r => r.opened_at) :
    filter === 'clicked'   ? recipients.filter(r => r.clicked_at) :
    recipients.filter(r => r.status === filter);

  const statusColor = { sent: '#1D9E75', failed: CHORD.coral, replied: '#2C7CE5', bounced: CHORD.orange };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#1F1F1B', border:`0.5px solid ${CHORD.border}`, borderRadius:16, width:'100%', maxWidth:700, maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <div style={{ padding:'1.25rem 1.5rem', borderBottom:`0.5px solid ${CHORD.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:CHORD.beige }}>{campaign.name}</div>
            <div style={{ fontSize:12, color:CHORD.textFaint, marginTop:2 }}>by {campaign.created_by_email} · {new Date(campaign.created_at).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:CHORD.textMuted, cursor:'pointer', fontSize:20, fontFamily:'inherit' }}>✕</button>
        </div>

        <div style={{ display:'flex', gap:8, padding:'1rem 1.5rem', borderBottom:`0.5px solid ${CHORD.border}`, flexWrap:'wrap' }}>
          {[
            ['Sent','sent','#1D9E75', recipients.filter(r=>r.status==='sent').length],
            ['Delivered','delivered','#2C7CE5', recipients.filter(r=>r.delivered_at).length],
            ['Opened','opened',CHORD.lime, recipients.filter(r=>r.opened_at).length],
            ['Clicked','clicked',CHORD.orange, recipients.filter(r=>r.clicked_at).length],
            ['Bounced','bounced',CHORD.coral, recipients.filter(r=>r.status==='bounced').length],
            ['Failed','failed','rgba(229,93,74,0.5)', recipients.filter(r=>r.status==='failed').length],
          ].map(([label, status, color, count]) => (
            <div key={status} onClick={() => setFilter(filter === status ? 'all' : status)}
              style={{ flex:1, minWidth:70, textAlign:'center', padding:'8px', borderRadius:8, cursor:'pointer', background: filter === status ? `${color}20` : 'transparent', border:`0.5px solid ${filter === status ? color : 'transparent'}` }}>
              <div style={{ fontSize:18, fontWeight:700, color }}>{count}</div>
              <div style={{ fontSize:10, color:CHORD.textMuted, textTransform:'uppercase' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Recipients list */}
        <div style={{ overflowY:'auto', flex:1, padding:'1rem 1.5rem' }}>
          {loading && <div style={{ color:CHORD.textFaint, fontSize:13 }}>Loading...</div>}
          {!loading && filtered.length === 0 && <div style={{ color:CHORD.textFaint, fontSize:13 }}>No recipients in this category.</div>}
          {filtered.map((r, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom:`0.5px solid ${CHORD.border}` }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, color:CHORD.beige, fontWeight:500 }}>{r.influencer_name}</div>
                <div style={{ fontSize:11, color:CHORD.textFaint }}>{r.email} · {r.platform?.toUpperCase()}</div>
              </div>
              <span style={{ background:`${statusColor[r.status]}20`, color:statusColor[r.status], borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:600, textTransform:'capitalize' }}>{r.status}</span>
              {r.error_message && <div style={{ fontSize:10, color:CHORD.coral, maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.error_message}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CampaignsPage() {
  const user = getUser();
  const [view, setView]           = useState('dashboard'); // dashboard | create
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [detailModal, setDetailModal] = useState(null);

  // Create flow state
  const [allInfluencers, setAllInfluencers] = useState([]);
  const [infLoading, setInfLoading]         = useState(false);
  const [selected, setSelected]             = useState(new Set());
  const [filters, setFilters]               = useState({ platform:'', tier:'', niche:'', city:'', search:'', hasEmail:true });
  const [campaignName, setCampaignName]     = useState('');
  const [subject, setSubject]               = useState(DEFAULT_SUBJECT);
  const [body, setBody]                     = useState(DEFAULT_BODY);
  const [step, setStep]                     = useState('select'); // select | compose | review | sending | done
  const [sendResult, setSendResult]         = useState(null);

  useEffect(() => { loadCampaigns(); }, []);

  async function loadCampaigns() {
    setLoading(true);
    try { const d = await getCampaigns(); setCampaigns(d.campaigns || []); }
    catch(e) {}
    finally { setLoading(false); }
  }

  async function loadInfluencers() {
    setInfLoading(true);
    try {
      const results = await Promise.all(
        PLATFORMS.map(p => fetchInfluencers(p.key, {})
          .then(r => (r.data || []).map(inf => ({ ...inf, _platform: p.key, _platformLabel: p.label, _platformColor: p.color })))
          .catch(() => []))
      );
      setAllInfluencers(results.flat());
    } catch(e) {}
    finally { setInfLoading(false); }
  }

  function startCreate() {
    setView('create'); setStep('select');
    setSelected(new Set()); setCampaignName('');
    setSubject(DEFAULT_SUBJECT); setBody(DEFAULT_BODY);
    setSendResult(null);
    if (allInfluencers.length === 0) loadInfluencers();
  }

  const filtered = allInfluencers.filter(inf => {
    if (filters.hasEmail && (!inf.email_contact || !inf.email_contact.includes('@'))) return false;
    if (filters.platform && inf._platform !== filters.platform) return false;
    if (filters.tier     && inf.tier !== filters.tier) return false;
    if (filters.niche    && !(inf.niche || '').toLowerCase().includes(filters.niche.toLowerCase())) return false;
    if (filters.city     && !(inf.city  || '').toLowerCase().includes(filters.city.toLowerCase())) return false;
    if (filters.search   && !(inf.influencer_name || '').toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const selectedInfluencers = allInfluencers.filter(i => selected.has(`${i._platform}_${i.id}`));

  function toggleSelect(key) { setSelected(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; }); }
  function selectAll()   { setSelected(new Set(filtered.map(i => `${i._platform}_${i.id}`))); }
  function deselectAll() { setSelected(new Set()); }

  async function handleSend() {
    setStep('sending');
    try {
      // Create campaign record first
      const camp = await createCampaign(campaignName || `Campaign ${new Date().toLocaleDateString('en-IN')}`, subject, body);
      const recipients = selectedInfluencers.map(i => ({ id: i.id, name: i.influencer_name, email: i.email_contact, platform: i._platform }));
      const res = await sendCampaign(camp.id, recipients, subject, body);
      setSendResult(res);
      setStep('done');
      loadCampaigns();
    } catch(e) {
      setSendResult({ error: e.message });
      setStep('done');
    }
  }

  const fi  = { background:'rgba(245,235,221,0.05)', border:`0.5px solid ${CHORD.border}`, borderRadius:6, padding:'6px 10px', fontSize:12, color:CHORD.beige, outline:'none', fontFamily:'inherit' };
  const sel = { ...fi, background:'#2B2B1F', cursor:'pointer', colorScheme:'dark' };

  // ── DASHBOARD ─────────────────────────────────────────────────────────────
  if (view === 'dashboard') return (
    <div>
      <div style={{ background:CHORD.softBlack, padding:'1.25rem 2rem', borderBottom:`1px solid ${CHORD.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, color:CHORD.beige, letterSpacing:'-0.3px', marginBottom:2 }}>Campaigns</div>
          <div style={{ fontSize:12, color:CHORD.textFaint }}>All email campaigns across the team</div>
        </div>
        <button onClick={startCreate} style={{ background:CHORD.coral, border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'inherit' }}>
          + New Campaign
        </button>
      </div>

      <div style={{ padding:'1.5rem 2rem' }}>
        {loading && <div style={{ color:CHORD.textFaint }}>Loading...</div>}
        {!loading && campaigns.length === 0 && (
          <div style={{ textAlign:'center', padding:'4rem', color:CHORD.textFaint }}>
            <div style={{ fontSize:32, marginBottom:12 }}>📧</div>
            <div style={{ fontSize:15, marginBottom:8, color:CHORD.beige }}>No campaigns yet</div>
            <div style={{ fontSize:13, marginBottom:20 }}>Create your first campaign to start reaching influencers</div>
            <button onClick={startCreate} style={{ background:CHORD.coral, border:'none', borderRadius:8, padding:'10px 24px', fontSize:13, fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'inherit' }}>
              + Create Campaign
            </button>
          </div>
        )}

        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {campaigns.map(c => (
            <div key={c.id} style={{ background:CHORD.bgCard, border:`0.5px solid ${CHORD.border}`, borderRadius:14, padding:'1.25rem 1.5rem' }}>
              {/* Campaign header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem' }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:CHORD.beige, marginBottom:4 }}>{c.name}</div>
                  <div style={{ fontSize:12, color:CHORD.textFaint }}>
                    by <strong style={{color:CHORD.beige}}>{c.created_by_email}</strong>
                    {' · '}{new Date(c.created_at).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                  </div>
                  <div style={{ fontSize:12, color:CHORD.textMuted, marginTop:4, fontStyle:'italic' }}>"{c.subject}"</div>
                </div>
                <span style={{ background: c.status === 'sent' ? 'rgba(29,158,117,0.15)' : 'rgba(245,235,221,0.06)', color: c.status === 'sent' ? '#1D9E75' : CHORD.textMuted, borderRadius:20, padding:'3px 12px', fontSize:11, fontWeight:600, textTransform:'uppercase' }}>
                  {c.status}
                </span>
              </div>

              {/* Stats */}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <StatBox label="Sent"      value={c.sent_count}       color="#1D9E75"     onClick={() => setDetailModal({...c, _filter:'sent'})} />
                <StatBox label="Delivered" value={c.delivered_count}  color="#2C7CE5"     onClick={() => setDetailModal({...c, _filter:'delivered'})} />
                <StatBox label="Opened"    value={c.opened_count}     color={CHORD.lime}  onClick={() => setDetailModal({...c, _filter:'opened'})} />
                <StatBox label="Clicked"   value={c.clicked_count}    color={CHORD.orange} onClick={() => setDetailModal({...c, _filter:'clicked'})} />
                <StatBox label="Bounced"   value={c.bounced_count}    color={CHORD.coral}  onClick={() => setDetailModal({...c, _filter:'bounced'})} />
                <StatBox label="Failed"    value={c.failed_count}     color="rgba(229,93,74,0.5)" onClick={() => setDetailModal({...c, _filter:'failed'})} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {detailModal && <DetailModal campaign={detailModal} onClose={() => setDetailModal(null)} />}
    </div>
  );

  // ── CREATE FLOW — DONE ────────────────────────────────────────────────────
  if (step === 'done') return (
    <div style={{ padding:'3rem', textAlign:'center', maxWidth:500, margin:'0 auto' }}>
      {sendResult?.error ? (
        <><div style={{ fontSize:36, marginBottom:16 }}>⚠️</div>
        <div style={{ fontSize:18, fontWeight:700, color:CHORD.coral, marginBottom:8 }}>Send Failed</div>
        <div style={{ fontSize:13, color:'rgba(245,235,221,0.5)' }}>{sendResult.error}</div></>
      ) : (
        <><div style={{ fontSize:36, marginBottom:16 }}>🎉</div>
        <div style={{ fontSize:20, fontWeight:700, color:CHORD.beige, marginBottom:16 }}>Campaign Sent!</div>
        <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
          <div style={{ background:'rgba(29,158,117,0.15)', border:'0.5px solid rgba(29,158,117,0.3)', borderRadius:10, padding:'12px 24px', textAlign:'center' }}>
            <div style={{ fontSize:28, fontWeight:700, color:'#1D9E75' }}>{sendResult.sent}</div>
            <div style={{ fontSize:11, color:'rgba(245,235,221,0.4)' }}>Sent</div>
          </div>
          {sendResult.failed > 0 && (
            <div style={{ background:'rgba(229,93,74,0.15)', border:'0.5px solid rgba(229,93,74,0.3)', borderRadius:10, padding:'12px 24px', textAlign:'center' }}>
              <div style={{ fontSize:28, fontWeight:700, color:CHORD.coral }}>{sendResult.failed}</div>
              <div style={{ fontSize:11, color:'rgba(245,235,221,0.4)' }}>Failed</div>
            </div>
          )}
        </div></>
      )}
      <div style={{ display:'flex', gap:10, justifyContent:'center', marginTop:'1.5rem' }}>
        <button onClick={() => { setView('dashboard'); loadCampaigns(); }} style={{ background:CHORD.coral, border:'none', borderRadius:8, padding:'10px 20px', fontSize:13, color:'#fff', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>View Dashboard</button>
        <button onClick={startCreate} style={{ background:'none', border:`0.5px solid ${CHORD.border}`, borderRadius:8, padding:'10px 20px', fontSize:13, color:CHORD.textMuted, cursor:'pointer', fontFamily:'inherit' }}>New Campaign</button>
      </div>
    </div>
  );

  // ── CREATE FLOW — SENDING ─────────────────────────────────────────────────
  if (step === 'sending') return (
    <div style={{ padding:'3rem', textAlign:'center' }}>
      <div style={{ fontSize:32, marginBottom:12 }}>⏳</div>
      <div style={{ fontSize:16, color:CHORD.beige }}>Sending campaign to {selectedInfluencers.length} influencers...</div>
      <div style={{ fontSize:12, color:CHORD.textFaint, marginTop:6 }}>Please wait, do not close this tab</div>
    </div>
  );

  // ── CREATE FLOW — REVIEW ──────────────────────────────────────────────────
  if (step === 'review') return (
    <div style={{ padding:'2rem', maxWidth:720, margin:'0 auto' }}>
      <button onClick={() => setStep('compose')} style={{ background:'none', border:'none', color:CHORD.textMuted, cursor:'pointer', fontSize:13, fontFamily:'inherit', marginBottom:'1.5rem' }}>← Back</button>
      <div style={{ fontSize:17, fontWeight:700, color:CHORD.beige, marginBottom:'1.5rem' }}>Review Before Sending</div>

      <div style={{ background:CHORD.bgCard, border:`0.5px solid ${CHORD.border}`, borderRadius:12, padding:'1rem 1.25rem', marginBottom:'1rem' }}>
        <div style={{ fontSize:11, color:CHORD.textMuted, textTransform:'uppercase', letterSpacing:'.8px', marginBottom:6 }}>Campaign</div>
        <div style={{ fontSize:14, color:CHORD.beige, fontWeight:600 }}>{campaignName || 'Untitled Campaign'}</div>
      </div>

      <div style={{ background:CHORD.bgCard, border:`0.5px solid ${CHORD.border}`, borderRadius:12, padding:'1rem 1.25rem', marginBottom:'1rem' }}>
        <div style={{ fontSize:11, color:CHORD.textMuted, textTransform:'uppercase', letterSpacing:'.8px', marginBottom:10 }}>
          Recipients — {selectedInfluencers.length} influencers
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, maxHeight:120, overflowY:'auto' }}>
          {selectedInfluencers.map(i => (
            <div key={`${i._platform}_${i.id}`} style={{ background:'rgba(245,235,221,0.06)', borderRadius:20, padding:'3px 12px', fontSize:11, color:CHORD.beige }}>
              {i.influencer_name} <span style={{ color:CHORD.textFaint }}>· {i._platformLabel}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background:CHORD.bgCard, border:`0.5px solid ${CHORD.border}`, borderRadius:12, padding:'1rem 1.25rem', marginBottom:'1rem' }}>
        <div style={{ fontSize:11, color:CHORD.textMuted, textTransform:'uppercase', letterSpacing:'.8px', marginBottom:6 }}>Subject</div>
        <div style={{ fontSize:14, color:CHORD.beige, fontWeight:500 }}>{subject}</div>
      </div>

      <div style={{ background:CHORD.bgCard, border:`0.5px solid ${CHORD.border}`, borderRadius:12, padding:'1rem 1.25rem', marginBottom:'1.5rem' }}>
        <div style={{ fontSize:11, color:CHORD.textMuted, textTransform:'uppercase', letterSpacing:'.8px', marginBottom:10 }}>
          Preview — {selectedInfluencers[0]?.influencer_name || 'First recipient'}
        </div>
        <div style={{ background:'#fff', borderRadius:8, padding:'20px', color:'#333', fontSize:13, lineHeight:1.7, whiteSpace:'pre-wrap' }}>
          {body.replace(/\{\{name\}\}/g, selectedInfluencers[0]?.influencer_name || '{{name}}')}
        </div>
      </div>

      <div style={{ display:'flex', gap:10 }}>
        <button onClick={handleSend} style={{ background:CHORD.coral, border:'none', borderRadius:8, padding:'11px 28px', fontSize:14, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'inherit' }}>
          🚀 Send to {selectedInfluencers.length} influencer{selectedInfluencers.length !== 1 ? 's' : ''}
        </button>
        <button onClick={() => setStep('compose')} style={{ background:'none', border:`0.5px solid ${CHORD.border}`, borderRadius:8, padding:'11px 20px', fontSize:13, color:CHORD.textMuted, cursor:'pointer', fontFamily:'inherit' }}>Edit</button>
      </div>
    </div>
  );

  // ── CREATE FLOW — COMPOSE ─────────────────────────────────────────────────
  if (step === 'compose') return (
    <div style={{ padding:'2rem', maxWidth:720, margin:'0 auto' }}>
      <button onClick={() => setStep('select')} style={{ background:'none', border:'none', color:CHORD.textMuted, cursor:'pointer', fontSize:13, fontFamily:'inherit', marginBottom:'1.5rem' }}>← Back to Selection</button>
      <div style={{ fontSize:17, fontWeight:700, color:CHORD.beige, marginBottom:'0.5rem' }}>Compose Email</div>
      <div style={{ fontSize:12, color:CHORD.textFaint, marginBottom:'1.5rem' }}>{selectedInfluencers.length} influencer{selectedInfluencers.length !== 1 ? 's' : ''} selected</div>

      <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
        <div>
          <label style={{ fontSize:11, color:CHORD.textMuted, display:'block', marginBottom:5 }}>Campaign Name</label>
          <input style={{ ...fi, width:'100%', boxSizing:'border-box' }} value={campaignName} onChange={e=>setCampaignName(e.target.value)} placeholder="e.g. Diwali 2026 — Fashion" />
        </div>
        <div>
          <label style={{ fontSize:11, color:CHORD.textMuted, display:'block', marginBottom:5 }}>Subject *</label>
          <input style={{ ...fi, width:'100%', boxSizing:'border-box', fontSize:13 }} value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Email subject..." />
        </div>
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
            <label style={{ fontSize:11, color:CHORD.textMuted }}>Message *</label>
            <span style={{ fontSize:10, color:CHORD.textFaint }}>{"{{name}}"} = influencer's name</span>
          </div>
          <textarea style={{ ...fi, width:'100%', boxSizing:'border-box', height:320, resize:'vertical', fontSize:13, lineHeight:1.6 }}
            value={body} onChange={e=>setBody(e.target.value)} />
        </div>
        <button onClick={() => setStep('review')} disabled={!subject.trim() || !body.trim()}
          style={{ background: subject && body ? CHORD.coral : 'rgba(245,235,221,0.08)', border:'none', borderRadius:10, padding:'12px', fontSize:14, fontWeight:700, color: subject && body ? '#fff' : 'rgba(245,235,221,0.25)', cursor: subject && body ? 'pointer' : 'not-allowed', fontFamily:'inherit' }}>
          Review Campaign →
        </button>
      </div>
    </div>
  );

  // ── CREATE FLOW — SELECT INFLUENCERS ─────────────────────────────────────
  return (
    <div>
      <div style={{ background:CHORD.softBlack, padding:'1.25rem 2rem', borderBottom:`1px solid ${CHORD.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <div style={{ fontSize:16, fontWeight:700, color:CHORD.beige }}>New Campaign — Select Influencers</div>
          <div style={{ fontSize:12, color:CHORD.textFaint, marginTop:2 }}>Filter and select influencers across all platforms</div>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <span style={{ fontSize:13, color: selected.size > 0 ? CHORD.coral : CHORD.textFaint, fontWeight:600 }}>{selected.size} selected</span>
          <button onClick={() => setView('dashboard')} style={{ background:'none', border:`0.5px solid ${CHORD.border}`, borderRadius:7, padding:'7px 14px', fontSize:12, color:CHORD.textMuted, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
          <button onClick={() => setStep('compose')} disabled={selected.size === 0}
            style={{ background: selected.size > 0 ? CHORD.coral : 'rgba(245,235,221,0.08)', border:'none', borderRadius:7, padding:'7px 16px', fontSize:12, fontWeight:600, color: selected.size > 0 ? '#fff' : 'rgba(245,235,221,0.25)', cursor: selected.size > 0 ? 'pointer' : 'not-allowed', fontFamily:'inherit' }}>
            Next: Compose →
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background:'#1F1F1B', padding:'0.75rem 2rem', borderBottom:`0.5px solid ${CHORD.border}`, display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
        <input style={{...fi,width:150}} placeholder="Search name..." value={filters.search} onChange={e=>setFilters(p=>({...p,search:e.target.value}))} />
        <select style={{...sel,width:130}} value={filters.platform} onChange={e=>setFilters(p=>({...p,platform:e.target.value}))}>
          <option value="">All platforms</option>
          {PLATFORMS.map(p=><option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select style={{...sel,width:110}} value={filters.tier} onChange={e=>setFilters(p=>({...p,tier:e.target.value}))}>
          <option value="">All tiers</option>
          {TIERS.map(t=><option key={t}>{t}</option>)}
        </select>
        <select style={{...sel,width:120}} value={filters.niche} onChange={e=>setFilters(p=>({...p,niche:e.target.value}))}>
          <option value="">All niches</option>
          {NICHES.map(n=><option key={n}>{n}</option>)}
        </select>
        <input style={{...fi,width:110}} placeholder="City..." value={filters.city} onChange={e=>setFilters(p=>({...p,city:e.target.value}))} />
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:CHORD.textMuted, cursor:'pointer' }}>
          <input type="checkbox" checked={filters.hasEmail} onChange={e=>setFilters(p=>({...p,hasEmail:e.target.checked}))} style={{ accentColor:CHORD.coral }} />
          Has email only
        </label>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button onClick={selectAll} style={{ fontSize:11, color:'#2C7CE5', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Select all ({filtered.length})</button>
          <span style={{ color:CHORD.border }}>·</span>
          <button onClick={deselectAll} style={{ fontSize:11, color:CHORD.textFaint, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Deselect all</button>
        </div>
      </div>

      {/* List */}
      <div style={{ padding:'1rem 2rem', overflowY:'auto', maxHeight:'calc(100vh - 220px)' }}>
        {infLoading && <div style={{ color:CHORD.textFaint, fontSize:13, padding:'2rem 0', textAlign:'center' }}>Loading influencers from all platforms...</div>}
        {!infLoading && filtered.length === 0 && <div style={{ color:CHORD.textFaint, fontSize:13, padding:'2rem 0', textAlign:'center' }}>No influencers match your filters.</div>}

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:8 }}>
          {filtered.map(inf => {
            const key        = `${inf._platform}_${inf.id}`;
            const isSelected = selected.has(key);
            const tierColor  = TIER_COLORS[inf.tier] || CHORD.textMuted;
            return (
              <div key={key} onClick={() => toggleSelect(key)} style={{
                display:'flex', alignItems:'center', gap:10,
                background: isSelected ? `${CHORD.coral}10` : 'rgba(245,235,221,0.02)',
                border:`0.5px solid ${isSelected ? CHORD.coral + '50' : CHORD.border}`,
                borderRadius:10, padding:'10px 14px', cursor:'pointer', transition:'all 0.12s',
              }}>
                <div style={{ width:18, height:18, borderRadius:5, flexShrink:0, background: isSelected ? CHORD.coral : 'transparent', border:`2px solid ${isSelected ? CHORD.coral : 'rgba(245,235,221,0.2)'}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {isSelected && <span style={{ color:'#fff', fontSize:11, fontWeight:700 }}>✓</span>}
                </div>
                <div style={{ width:32, height:32, borderRadius:'50%', flexShrink:0, background:`${inf._platformColor}22`, border:`1.5px solid ${inf._platformColor}40`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:inf._platformColor }}>
                  {inf.influencer_name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:CHORD.beige, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inf.influencer_name}</div>
                  <div style={{ fontSize:11, color: inf.email_contact ? CHORD.textFaint : 'rgba(229,93,74,0.5)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {inf.email_contact || 'No email'}
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2, flexShrink:0 }}>
                  <span style={{ fontSize:10, color:inf._platformColor, fontWeight:600 }}>{inf._platformLabel}</span>
                  <span style={{ background:`${tierColor}22`, color:tierColor, borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:600 }}>{inf.tier}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
